import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/Modal';
import { useUser } from '@/features/auth/hooks';
import { useEmailVerifyResendMutation } from '@/features/auth/api';
import { TurnstileWidget } from './TurnstileWidget';

/*
 * EmailVerifyBanner (Sessio 6b -> Sessio 7a: Turnstile-modaali).
 *
 * Renderoidaan AppLayout:n paaalueen ylaosaan kun kirjautunut kayttaja ei ole
 * viela vahvistanut email-osoitettaan (`user.email_verified_at === null`).
 *
 * Klikkaus "Laheta linkki uudelleen" avaa modaalin jossa Cloudflare Turnstile
 * -widget. Kun token on saatu, kayttaja klikkaa "Laheta" -painiketta -> POST
 * /auth/email/verify/send Turnstile-tokenilla. Onnistunut lahetys nayttaa
 * kuittauksen + (dev-tilassa) suoran linkin testausta varten.
 *
 * Banner katoaa automaattisesti kun kayttaja palaa kotiin verify-flow:n jalkeen
 * -- AuthContext paivittaa user.email_verified_at:n bootstrap-refresh:n kautta.
 */
export function EmailVerifyBanner() {
  const { t } = useTranslation();
  const user = useUser();
  const resend = useEmailVerifyResendMutation();
  const [open, setOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  if (!user || user.email_verified_at !== null) {
    return null;
  }

  const handleSubmit = () => {
    if (!turnstileToken) return;
    resend.mutate({ turnstile_token: turnstileToken });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Modaali sulkeutuu -> nollaa tila seuraavaa kertaa varten
      setTurnstileToken('');
      resend.reset();
    }
  };

  return (
    <div
      role="region"
      aria-label={t('auth.verify.banner.regionLabel')}
      className="border-b border-keltainen bg-keltainen/15 px-6 py-3 text-sm text-teksti lg:px-10"
    >
      <div className="mx-auto flex max-w-[var(--content-max)] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <strong className="font-semibold">{t('auth.verify.banner.title')}</strong>
          <span className="ml-2 text-usva-t">{t('auth.verify.banner.body')}</span>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
          {t('auth.verify.banner.action')}
        </Button>
      </div>

      <Modal open={open} onOpenChange={handleOpenChange}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{t('auth.verify.modal.title')}</ModalTitle>
            <ModalDescription>{t('auth.verify.modal.body')}</ModalDescription>
          </ModalHeader>

          <div className="flex flex-col items-center gap-3">
            {resend.isSuccess ? (
              <p role="status" className="text-sm text-sammal">{t('auth.verify.resendSent')}</p>
            ) : (
              <TurnstileWidget
                onToken={setTurnstileToken}
                onError={() => setTurnstileToken('')}
                resetSignal={resend.failureCount}
              />
            )}
            {resend.isError && (
              <p role="alert" className="text-xs text-oranssi">
                {t('auth.verify.resendFailed')}
              </p>
            )}
            {resend.data?.dev_preview?.verification_link && (
              <a
                href={resend.data.dev_preview.verification_link}
                className="max-w-xs truncate text-xs text-usva-t underline"
              >
                {resend.data.dev_preview.verification_link}
              </a>
            )}
          </div>

          <ModalFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {t('common.close')}
            </Button>
            {!resend.isSuccess && (
              <Button
                variant="secondary"
                onClick={handleSubmit}
                disabled={!turnstileToken || resend.isPending}
              >
                {resend.isPending
                  ? t('auth.verify.resendPending')
                  : t('auth.verify.modal.submit')}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
